import React, { useMemo, useState } from 'react'
import { Drawer, Upload, Typography, message, TreeSelect } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import { useFFEStore } from '../../../store/ffeStore'
import { useApi } from '../../../services/ApiContext'
import { UploadChangeParam, UploadFile } from 'antd/es/upload'

const { Dragger } = Upload
const { Text } = Typography

type Children = Array<{
  title: string
  value: string
  disabled?: boolean
  children: Children
}>

interface Props {
  open: boolean
  onClose: () => void
}

export const BulkImageUploadDrawer: React.FC<Props> = ({ open, onClose }) => {
  const [uploading, setUploading] = useState(false)
  const addLineItem = useFFEStore((state) => state.addLineItem)
  const groups = useFFEStore((state) => state.document.groups)
  const defaultMargin = useFFEStore((state) => state.document.settings.defaultMargin)

  const api = useApi()

  const selectedGroupId = useFFEStore((state) => state.bulkImageUploadDrawerId)
  const setSelectedGroupId = useFFEStore(
    (state) => state.setBulkImageUploadDrawerId
  )

  const getGroupTree = (groupId?: string): Children => {
    const group = groups.find((group) => group.id === groupId)
    if (!group) return []

    const children = groups.filter((group) => group.groupId === groupId)
    return children.map((child) => ({
      title: child.name,
      value: child.id,
      children: getGroupTree(child.id)
    }))
  }

  const treeGroups = useMemo(() => {
    return groups
      .filter((group) => !group.groupId)
      .map((group) => ({
        title: group.name,
        value: group.id,
        children: getGroupTree(group.id)
      }))
  }, [groups])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleUpload = async ({ file, onSuccess }: any) => {
    try {
      setUploading(true)

      if (!(file instanceof File)) {
        throw new Error('Invalid file')
      }

      // Create object URL for preview
      const objectUrl = await api.uploadImage(file)

      // Generate name from file name
      const name = file.name
        .replace(/\.[^/.]+$/, '') // Remove extension
        .split(/[-_]/) // Split on hyphens and underscores
        .map(
          (
            word: string // Capitalize words
          ) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' ')

      // Create new line item
      addLineItem({
        name,
        description: '',
        quantity: 1,
        costPrice: 0,
        margin: defaultMargin,
        sellPrice: 0,
        status: 'specified',
        groupId: selectedGroupId,
        image: {
          url: objectUrl,
          alt: name
        }
      })

      onSuccess?.(null)
      // message.success(`Created line item: ${name}`)
    } catch (error) {
      console.log(error)
      message.error('Failed to create line item')
    } finally {
      setUploading(false)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleUploadChange = (info: UploadChangeParam<UploadFile<any>>) => {
    if (info.fileList.length > 0) {
      const waitingFiles = info.fileList.filter(
        (file) => file.status !== 'done'
      )
      if (waitingFiles.length === 0) {
        message.success({
          key: 'uploading',
          content: 'All files uploaded',
          duration: 5
        })
      } else {
        message.loading({
          type: 'loading',
          key: 'uploading',
          content: `${waitingFiles.length} file${waitingFiles.length > 1 ? 's' : ''} in progress...`
        })
      }
    }
  }

  return (
    <Drawer
      title="Bulk Image Upload"
      placement="right"
      width={600}
      open={open}
      onClose={onClose}
    >
      <div className="space-y-6">
        <Text className="block mb-4">
          Upload multiple images to automatically create line items. The file
          names will be used as item titles.
        </Text>

        <div className="block !mb-8">
          <Text type="secondary" className="block mb-2">
            Place items into group:
          </Text>
          <TreeSelect
            showSearch
            value={selectedGroupId}
            placeholder="Select a group"
            className="w-full"
            autoFocus
            allowClear
            treeDefaultExpandAll
            onChange={(value) => setSelectedGroupId(value)}
            treeData={treeGroups}
          />
        </div>

        <Dragger
          multiple
          customRequest={handleUpload}
          accept="image/*"
          disabled={uploading}
          onChange={handleUploadChange}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag images to this area to upload
          </p>
          <p className="ant-upload-hint">
            Supports multiple images. File names will be converted to item
            titles.
          </p>
        </Dragger>

        <div className="text-gray-500 text-sm">
          <Text strong className="block mb-2">
            Tips:
          </Text>
          <ul className="list-disc pl-4 space-y-1">
            <li>Use descriptive file names for better item titles</li>
            <li>Images will be automatically attached to the created items</li>
            <li>You can edit the items after creation</li>
          </ul>
        </div>
      </div>
    </Drawer>
  )
}
